import {
    createContext,
    useState,
    useEffect,
    useContext,
} from "react";
import { useRouter } from "next/router";
import BusinessContext from "./business-context";
import { addRecentfiles, fetchFiles, renderRecentFiles, uploadFile, deleteFile, updateFile, onCreateFolder, onRenderFolders, updateFolder, deleteFolder, getFileIndividualPermissions, updateFileIndividualPermissions } from "../lib/files";
import { fileTypeFinder, generateUniqueFileName } from "../lib/utils";
import { getAPIURL } from "../lib/utils";
import { set } from "date-fns";
import { render } from "katex";

const FileContext = createContext({
    files: null,
    setFiles: () => { },
    getFiles: () => { },
    getFolders: () => { },
    fileList: [],
    folderList: [],
    createNewFile: () => { },
    createFolder: () => { },
    dir: null,
    setDir: () => { },
    addToRecentFiles: () => { },
    recentFiles: null,
    setRecentFiles: () => { },
    recentFilesToDisplay: null,
    setRecentFilesToDisplay: () => { },
    fetchRecentFiles: () => { },
    recentFolderToDisplay: null,
    setRecentFolderToDisplay: () => { },
    deleteFileHandler: () => { },
    deleteFolderHandler: () => { },
    handleTrashAction: () => { },
    isCreateFolder: false,
    toggleCreateFolder: () => { },
    setIsCreateFolder: () => { },
    filteredFiles: null,
    setFilteredFiles: () => { },
    allFiles: null,
    setAllFiles: () => { },
    // getAllFiles: () => { },
    filterSearchBy: null,
    setFilterSearchBy: () => { },
    activeFileTypeFilter: null,
    setActiveFileTypeFilter: () => { },
    filteredFileList: null,
    filteredFolderList: null,
    handleFileUpdate: () => { },
    handleFolderUpdate: () => { },
    currentPath: null,
    renderFile: () => { },
    // Selection context properties
    selectedFiles: [],
    isSelecting: false,
    toggleSelection: () => { },
    toggleFileSelection: () => { },
    clearSelection: () => { },
    deleteSelectedFiles: () => { },
    selectAllFiles: () => { },
    downloadSelectedFiles: () => { },
    isSelectedFilesDownloadable: false,
    setIsSelectedFilesDownloadable: () => { },
    // Upload progress tracking
    uploadingFiles: [],
    addUploadFile: () => { },
    updateUploadProgress: () => { },
    completeUpload: () => { },
    failUpload: () => { },
    clearUploads: () => { },
    updateIndividualPermissions: () => {}
});

export const FileProvider = (props) => {
    const router = useRouter();
    const { activeBusiness, setNotificationContent } = useContext(BusinessContext);
    const [files, setFiles] = useState(null);
    const [listOfFolders, setListOfFolders] = useState(null);
    const [allFiles, setAllFiles] = useState(null);
    const [dir, setDir] = useState(null);
    const [recentFiles, setRecentFiles] = useState(null);
    const [recentFilesToDisplay, setRecentFilesToDisplay] = useState(null);
    const [recentFolderToDisplay, setRecentFolderToDisplay] = useState(null);
    const [isCreateFolder, setIsCreateFolder] = useState(false);
    const [filteredFiles, setFilteredFiles] = useState(null);
    const [filterSearchBy, setFilterSearchBy] = useState(null);
    const [activeFileTypeFilter, setActiveFileTypeFilter] = useState('all');
    const [currentPath, setCurrentPath] = useState(null);
    // Selection state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [isSelectedFilesDownloadable, setIsSelectedFilesDownloadable] = useState(false);

    const [spacePath, setSpacePath] = useState(null);

    // Upload tracking state
    const [uploadingFiles, setUploadingFiles] = useState([]);

    // Filter type mapping
    const filterTypeMap = {
        'all': 'all',
        'documents': 'document',
        'photos': 'image',
        'videos': 'video',
        'audios': 'audio',
        'folders': 'folder',
        'spreadsheets': 'spreadsheet',
        'zip': 'zip'
    };

    useEffect(() => {
        setFiles(null)
    }, [router])

    useEffect(() => {
        if (dir && listOfFolders) {
            const currentFolder = listOfFolders.find(folder => folder.id === dir);
            if (currentFolder) {
                // Extract just the friendly name from the namespace
                const nameParts = currentFolder.namespace.split('/');
                const friendlyName = nameParts[nameParts?.length - 1];
                setCurrentPath(friendlyName);
            } else {
                setCurrentPath(null);
            }
        } else {
            setCurrentPath(null);
        }
    }, [dir, listOfFolders]);

    // Base file and folder lists
    const baseFileList = files ? files.filter(file => {
        if (dir) {
            const targetFolderId = listOfFolders?.find(folder => folder.id === dir)?.id;
            return file?.path?.id === targetFolderId;
        } else {
            return file?.path === dir;
        }
    }) : null;
    const baseFolderList = listOfFolders ? listOfFolders.filter(folder => {
        // Skip folders with namespace "__emails"
        if (!folder.namespace || folder.namespace === "__emails") return false;

        const folderSegments = folder.namespace.split('/').filter(segment => segment?.length > 0);

        if (!dir) {
            return folderSegments?.length === 1;
        } else {
            const currentDirSegments = dir.split('/').filter(segment => segment?.length > 0);

            return folderSegments?.length === currentDirSegments?.length + 1 &&
                folder.namespace.startsWith(dir + '/');
        }
    }) : null;


    // Filtered file and folder lists
    const filteredFileList = activeFileTypeFilter
        ? (activeFileTypeFilter === 'folders'
            ? []
            : activeFileTypeFilter === 'all'
                ? baseFileList
                : baseFileList?.filter(file => fileTypeFinder(file) === filterTypeMap[activeFileTypeFilter]))
        : baseFileList;

    const filteredFolderList = activeFileTypeFilter && activeFileTypeFilter !== 'folders' && activeFileTypeFilter !== 'all'
        ? []
        : baseFolderList;

    const toggleCreateFolder = () => {
        setIsCreateFolder(!isCreateFolder);
    }

    // create folder function
    const createFolder = (payload, additionalCallback, additionalError) => {

        const callBack = (json) => {
            { additionalCallback && additionalCallback(json); }
        }
        const onError = (error) => {
            console.error('Error creating folder:', error);
            { additionalError && additionalError(error); }
        }

        onCreateFolder(payload, callBack, activeBusiness, onError, router);

    }

    // fetch all folders
    const getFolders = () => {
        const callBack = (json) => {
            setListOfFolders(json);
        }
        const onError = (error) => {
            console.error('Error fetching folders:', error);
        }
        onRenderFolders(callBack, activeBusiness, onError, router);
    }

    // Selection functions
    const toggleSelection = () => {
        if (selectedFiles?.length === 0) {
            setIsSelecting(false);
        } else {
            setIsSelecting(true);
        }
    };

    const toggleFileSelection = (file) => {
        setSelectedFiles(prevSelectedFiles => {
            const isSelected = prevSelectedFiles.some(f => f.id === file.id);

            let newSelection;
            if (isSelected) {
                newSelection = prevSelectedFiles.filter(f => f.id !== file.id);
            } else {
                newSelection = [...prevSelectedFiles, file];
            }

            if (newSelection?.length > 0 && !isSelecting) {
                setIsSelecting(true);
            }

            if (newSelection?.length === 0) {
                setIsSelecting(false);
            }

            return newSelection;
        });
    };

    const clearSelection = () => {
        setSelectedFiles([]);
        setIsSelecting(false);
    };

    const deleteSelectedFiles = () => {
        selectedFiles.forEach(file => {
            if (file.alias) {
                deleteFileHandler(file.id);
            }
            else {
                deleteFolderHandler(file.id);
            }
        });
        clearSelection();
    }


    const selectAllFiles = () => {
        // Get files based on current view
        const isTrashView = router.pathname === '/trash';

        let allCurrentFiles;
        if (isTrashView) {
            allCurrentFiles = files || [];
        } else {
            const filesInCurrentDir = files ? files.filter(file => {
                if (dir) {
                    return file?.path?.id === dir;
                } else {
                    return file?.path === null;
                }
            }) : [];

            allCurrentFiles = [...filesInCurrentDir, ...baseFolderList];
        }

        // Check if all files are already selected
        const allFilesSelected = allCurrentFiles?.length === selectedFiles?.length &&
            allCurrentFiles.every(file =>
                selectedFiles.some(selectedFile => selectedFile.id === file.id)
            );

        if (allFilesSelected) {
            // If all files are selected, clear selection
            clearSelection();
        } else {
            setSelectedFiles(allCurrentFiles);
            setIsSelecting(true);
        }
    }

    useEffect(() => {
        if (selectedFiles.some(file => file.namespace)) {
            setIsSelectedFilesDownloadable(false);
        } else if (selectedFiles?.length > 0) {
            setIsSelectedFilesDownloadable(true);
        } else if (selectedFiles.length === 0) {
            setIsSelectedFilesDownloadable(false);
        }
    }, [selectedFiles]);

    const downloadSelectedFiles = async () => {
        clearSelection();
        for (let i = 0; i < selectedFiles?.length; i++) {
            const file = selectedFiles[i];
            const link = document.createElement('a');
            link.href = getAPIURL(`businesses/${activeBusiness?.uuid}/files/${file.id}/link/`);
            link.download = file.alias;

            // Add event listener to wait for download to complete
            link.addEventListener('click', () => {
                return new Promise(resolve => {
                    setTimeout(() => {
                        document.body.removeChild(link);
                        resolve();
                    }, 1000);
                });
            });

            document.body.appendChild(link);
            link.click();

            // Wait longer between downloads
            await new Promise(resolve => setTimeout(resolve, 2000));
        }
    };

    useEffect(() => {

        clearSelection();
        getFolders();
    }, [router]);

    useEffect(() => {
        if (recentFiles && recentFiles?.length > 0 && files) {
            // Create a map of file ids for quick lookup
            const fileIdsMap = files.reduce((map, file) => {
                map[file.id] = true;
                return map;
            }, {});

            // Process recent files, ensure they exist in files array and aren't trashed
            const processedItems = recentFiles
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .filter((item, index, self) =>
                    // Filter duplicates based on file id
                    index === self.findIndex((t) => t.file.id === item.file.id) &&
                    // Ensure file exists in files array
                    fileIdsMap[item.file.id] &&
                    // Ensure file is not trashed
                    item.file.trash_status !== 1
                )
                .map(recentItem => ({
                    ...recentItem.file,
                    created_at: recentItem.created_at
                }));

            setRecentFilesToDisplay(processedItems.slice(0, 6));
        } else {
            setRecentFilesToDisplay(null);
        }
    }, [recentFiles, files]);

    // const getAllFiles = (additionalCallback) => {
    //     const callBack = (json) => {
    //         { additionalCallback && additionalCallback(json); }
    //         setAllFiles(json);
    //     }
    //     const onError = (error) => {
    //         console.error('Error fetching files:', error);
    //     }

    //     fetchFiles(callBack, onError, activeBusiness, router);
    // }

    const getFiles = (dir) => {
        const directory = dir ? `?${dir}` : null;
        const callBack = (json) => {
            setFiles(json);
        }
        const onError = (error) => {
            console.error('Error fetching files:', error);
        }

        fetchFiles(callBack, onError, activeBusiness, router, `?parent=${dir ? dir : spacePath.id}`);
    }

    const createNewFile = (file, additionalCallback, additionalError, payload, dir) => {
        const formData = new FormData();
        const activeFiles = files.filter(f => f.trash_status !== 1);
        const fileName = generateUniqueFileName(file, activeFiles);
        formData.append('file', file);
        formData.append('alias', fileName);
        formData.append('path', listOfFolders.find(folder => folder.id === spacePath.id)?.id);

        // Add file to upload tracking
        let fileId;
        let progressInterval;

        // if (file) {
        //     fileId = addUploadFile(file);
        //     progressInterval = setInterval(() => {
        //         setUploadingFiles(prev => {
        //             const updatedFiles = [...prev];
        //             const fileIndex = updatedFiles.findIndex(f => f.id === fileId);

        //             if (fileIndex !== -1 && updatedFiles[fileIndex].status === 'uploading') {
        //                 const randomIncrease = Math.max(1, Math.floor(Math.random() * 5));
        //                 const currentProgress = updatedFiles[fileIndex].progress;
        //                 const newProgress = Math.min(99, currentProgress + randomIncrease);
        //                 const remainingProgress = 100 - newProgress;
        //                 const timeRemaining = Math.max(1, Math.floor((remainingProgress / 10) * 3));

        //                 updatedFiles[fileIndex] = {
        //                     ...updatedFiles[fileIndex],
        //                     progress: newProgress,
        //                     timeRemaining: timeRemaining
        //                 };
        //             }

        //             return updatedFiles;
        //         });
        //     }, 1000);
        // }

        const callBack = (json) => {
            { additionalCallback && additionalCallback(json); }
            if (file) {
                clearInterval(progressInterval);
                completeUpload(fileId);
                // setNotificationContent({
                //     showNotification: true,
                //     header: 'File Uploaded',
                //     message: 'Your files has been uploaded successfully.',
                //     icon: 'check',
                // });
                // setTimeout(() => {
                //     setNotificationContent(prev => ({ ...prev, showNotification: false }));
                // }, 5000);
            }
            // Keep existing functionality
            getFiles();
        };

        const onError = (json) => {
            if (file) {
                clearInterval(progressInterval);
                failUpload(fileId);
            }

            console.error('Error uploading file:', json);
            { additionalError && additionalError(json); }
        };

        uploadFile(payload ? payload : formData, callBack, onError, activeBusiness, router);
    }


    const deleteFileHandler = (fileId) => {
        const callBack = () => {
            setFiles(prev => prev.filter(file => file.id !== fileId));
        }
        const onError = (json) => {
            console.error('Error deleting file:', json);
        }
        deleteFile(fileId, callBack, onError, activeBusiness, router);
    }

    const deleteFolderHandler = (pathId) => {
        const callBack = () => {
            setListOfFolders(prev => prev.filter(folder => folder.id !== pathId));
        }
        const onError = (json) => {
            console.error('Error deleting file:', json);
        }
        deleteFolder(pathId, callBack, onError, activeBusiness, router);
    }

    const handleTrashAction = (fileId, payload, additionalCallback) => {
        const callBack = (json) => {
            { additionalCallback && additionalCallback(json); }
        }
        const onError = (json) => {
            console.error('Error deleting file:', json);
        }

        updateFile(fileId, payload, callBack, onError, activeBusiness, router);
    }

    const handleFileUpdate = (fileId, fileUpdate, additionalCallback) => {

        const callBack = (json) => {
            { additionalCallback && additionalCallback(json); }
        }
        const onError = (err) => {
            console.error(err)
        }
        updateFile(fileId, fileUpdate, callBack, onError, activeBusiness, router)
    }

    const handleFolderUpdate = (folderId, folderUpdate, additionalCallback, additionalError) => {
        const callBack = (json) => {
            { additionalCallback && additionalCallback(json); }
        }
        const onError = (err) => {
            { additionalError && additionalError(err); }
            console.error(err)
        }
        updateFolder(folderId, folderUpdate, callBack, onError, activeBusiness, router)
    }

    // Upload tracking functions
    const addUploadFile = (file) => {
        const uploadFile = {
            id: Date.now() + Math.random().toString(36),
            name: file.name,
            size: file.size,
            progress: 0,
            status: 'uploading',
            type: file.type.split('/')[0] || 'file',
            timeRemaining: 30, // default time in seconds
            extension: file.name.split('.').pop() || 'file',
            file: file,
        };

        setUploadingFiles(prev => [...prev, uploadFile]);
        return uploadFile.id;
    };

    const updateUploadProgress = (fileId, progress) => {
        setUploadingFiles(prev => {
            return prev.map(f =>
                f.id === fileId && f.status === 'uploading' ? {
                    ...f,
                    progress: Math.min(progress, 99),
                    timeRemaining: Math.max(1, Math.floor(((100 - progress) / 10) * 3))
                } : f
            );
        });
    };

    const completeUpload = (fileId) => {
        // First set progress to 100% but keep status as uploading
        setUploadingFiles(prev => prev.map(f =>
            f.id === fileId ? { ...f, progress: 100, timeRemaining: 0 } : f
        ));
        setTimeout(() => {
            setUploadingFiles(prev => prev.map(f =>
                f.id === fileId ? { ...f, status: 'complete', progress: 100, timeRemaining: 0 } : f
            ));
        }, 500);
    };

    const failUpload = (fileId) => {
        setUploadingFiles(prev => prev.map(f =>
            f.id === fileId ? { ...f, status: 'error' } : f
        ));
    };

    const clearUploads = () => {
        setUploadingFiles([]);
    };

    const renderFile = (fileId) => {
        return files?.find(file => file.id === fileId);
    }

    const fetchSpacePath = () => {

        const spaceFolder = listOfFolders?.find(folder => folder.namespace === '__general_dir');
        if (spaceFolder) {
            setSpacePath(spaceFolder);
            return;
        }

        const callBack = (json) => {
            setSpacePath(json);
        }
        const onError = (error) => {
            console.error('Error fetching space path:', error);
        }
        createFolder({ namespace: '__general_dir', parent: null }, callBack, onError);
    }

    const updateIndividualPermissions = (fileId, permission) => {
        updateFileIndividualPermissions(fileId, permission, null, null, activeBusiness, router)
    }

    const renderFileIndividualPermission = (fileId) => {
        const callback = (json) => {
            return json;
        }
        const onError = (error) => {
            console.error('Error fetching individual file permission:', error);
        }

        getFileIndividualPermissions(fileId, callback, onError, activeBusiness, router);
    }

    const syncIndividualPermission = (fileId, staffs) => {

    }

    useEffect(() => {
        if (activeBusiness) {
            if (listOfFolders && !spacePath) {
                fetchSpacePath();
            }
            if (spacePath) {
                getFiles();
            }
        }
    }, [listOfFolders, spacePath, activeBusiness]);


    const context = {
        files,
        setFiles,
        getFiles,
        getFolders,
        fileList: baseFileList,
        folderList: baseFolderList,
        listOfFolders,
        createNewFile,
        createFolder,
        dir,
        setDir,
        setRecentFiles,
        recentFiles,
        recentFilesToDisplay,
        setRecentFilesToDisplay,
        deleteFileHandler,
        deleteFolderHandler,
        handleTrashAction,
        isCreateFolder,
        toggleCreateFolder,
        setIsCreateFolder,
        filteredFiles,
        setFilteredFiles,
        allFiles,
        setAllFiles,
        // getAllFiles,
        filterSearchBy,
        setFilterSearchBy,
        activeFileTypeFilter,
        setActiveFileTypeFilter,
        filteredFileList,
        filteredFolderList,
        handleFileUpdate,
        handleFolderUpdate,
        currentPath,
        listOfFolders,
        // Selection context
        selectedFiles,
        isSelecting,
        toggleSelection,
        toggleFileSelection,
        clearSelection,
        deleteSelectedFiles,
        selectAllFiles,
        downloadSelectedFiles,
        isSelectedFilesDownloadable,
        // Upload progress tracking
        uploadingFiles,
        addUploadFile,
        updateUploadProgress,
        completeUpload,
        failUpload,
        clearUploads,
        renderFile,
        updateIndividualPermissions
    };

    return (
        <FileContext.Provider value={context}>
            {props.children}
        </FileContext.Provider>
    );
};

export default FileContext;